import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Upload, Download, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const KINDS = {
  staff: { label: "Staff & Drivers", perm: "users:manage", cols: "name,email,role,phone", sample: "Jane Doe,jane@example.com,DRIVER,614-555-0100", note: "Role must be STAFF or DRIVER. Imported users are INVITED — set a password in Users before they can sign in." },
  customers: { label: "Customers", perm: "customers:manage", cols: "name,phone,email,address_line1,city,state,zip,service_tags,wheelchair,notes", sample: "John Smith,614-555-0111,,123 Main St,Columbus,OH,43215,NMT;Adult Day Support,yes,", note: "service_tags separated by semicolons. Duplicates match on name + phone." },
  vehicles: { label: "Vehicles", perm: "fleet:manage", cols: "code,name,make,model,year,vin,license_plate,capacity,wheelchair_accessible,status", sample: "LFM-020,Transit Van,Ford,Transit,2023,,ABC1234,8,yes,ACTIVE", note: "Duplicates match on fleet code or VIN." },
};

export default function Imports() {
  const { hasPerm } = useAuth();
  const allowed = Object.entries(KINDS).filter(([, k]) => hasPerm(k.perm));
  const [kind, setKind] = useState(allowed[0]?.[0] || "customers");
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (commit) => {
    if (!file) { toast.error("Choose a CSV file"); return; }
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(`/imports/${kind}`, fd, { params: { commit } });
      setResult(data);
      if (commit) toast.success(`${data.created} records imported`);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const template = () => {
    const blob = new Blob([`${KINDS[kind].cols}\n${KINDS[kind].sample}\n`], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${kind}_template.csv`; a.click();
  };

  return (
    <div data-testid="imports-page" className="max-w-4xl">
      <PageHeader title="Data Import" subtitle="Bulk-add staff, customers and vehicles from CSV. Preview first — nothing is saved until you confirm." />
      <div className="flex flex-wrap gap-2 mb-4">
        {allowed.map(([k, v]) => <Button key={k} variant={kind === k ? "default" : "outline"} size="sm" onClick={() => { setKind(k); setResult(null); }} data-testid={`import-kind-${k}`}>{v.label}</Button>)}
      </div>
      <div className="border border-border rounded-lg bg-card p-5 space-y-3">
        <p className="text-sm"><b>Columns:</b> <code className="text-xs">{KINDS[kind].cols}</code></p>
        <p className="text-xs text-muted-foreground">{KINDS[kind].note}</p>
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" size="sm" onClick={template} data-testid="import-template-btn"><Download className="w-4 h-4 mr-1" /> Template</Button>
          <input type="file" accept=".csv,text/csv" onChange={(e) => { setFile(e.target.files[0]); setResult(null); }} className="text-sm" data-testid="import-file-input" />
        </div>
        <div className="flex gap-2">
          <Button onClick={() => run(false)} disabled={busy || !file} data-testid="import-preview-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Preview</>}</Button>
          {result && !result.committed && result.valid > 0 && <Button variant="outline" onClick={() => run(true)} disabled={busy} data-testid="import-commit-btn">Import {result.valid} records</Button>}
        </div>
      </div>
      {result && (
        <div className="mt-5 space-y-3" data-testid="import-result">
          <div className="flex flex-wrap gap-4 text-sm">
            <span className="flex items-center gap-1 text-success"><CheckCircle2 className="w-4 h-4" /> {result.committed ? `${result.created} imported` : `${result.valid} ready`}</span>
            <span data-testid="import-duplicates">{result.duplicates} duplicates skipped</span>
            <span className="flex items-center gap-1 text-danger" data-testid="import-errors-count"><AlertTriangle className="w-4 h-4" /> {result.errors.length} errors</span>
          </div>
          {result.errors.length > 0 && <ul className="text-xs text-danger space-y-0.5">{result.errors.map((e) => <li key={`${e.row}-${e.error}`}>Row {e.row}: {e.error}</li>)}</ul>}
          {result.preview.length > 0 && (
            <div className="overflow-x-auto border border-border rounded-md">
              <table className="text-xs w-full"><tbody>
                {result.preview.map((p) => <tr key={p.id} className="border-b border-border"><td className="p-2 font-semibold">{p.name || p.code}</td><td className="p-2 text-muted-foreground">{p.email || p.phone || p.make || ""}</td><td className="p-2">{p.role || (p.addresses ? `${p.addresses.length} address` : p.capacity ? `cap ${p.capacity}` : "")}</td></tr>)}
              </tbody></table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
